import { Alert, Button, Checkbox, Spacer } from '@ksyos/design-system';
import { OrderedMap } from 'immutable';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { IPatient } from '@common/interfaces/patient';
import type { IEpisodesAndJournals, IJournal } from '@common/validation/episodesAndJournal';
import type { IJournalEntry } from '@frontend/components/interfaces/journalEntry';
import type { IEpisode } from '@frontend/components/interfaces/patientEpisode';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ProfessionalSummaryDataType } from '@common/enums/professionalSummaryDataType';
import {
    LinkOnClick,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '@frontend/components/elements/common';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { EpisodeJournalsCanAdd } from '@frontend/components/pages/customControls/utils/v1/episodeJournals/EpisodeJournalsCanAdd';
import { ProfessionalSummaryDataTable } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataTable';
import { isHisDataAvailable } from '@frontend/services/api/patientsApiService';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import type { EpisodeRecord, Episodes, JournalEntries } from './episodeJournals';
import { ProfessionalSummaryDataPlaceholder } from '../../../../professionalSummaryData/ProfessionalSummaryDataPlaceholder';
import {
    ProfessionalSummaryDataAction,
    ProfessionalSummaryDataSection,
} from '../../../../professionalSummaryData/ProfessionalSummaryDataSection';
import { checkJournalsHaveErrors } from '../../../validateJournals';
import { EpisodeJournalEntryAddEdit } from './EpisodeJournalEntryAddEdit';
import {
    filterAndPrepareEpisodesForSaving,
    isCurrentEpisode,
    loadProcessActivityEpisodes,
    loadRequestEpisodes,
    prepareAndSetJournalEntries,
    prepareEpisodesForSaving,
} from './episodeJournals';

interface IEpisodeJournalsAddEditProps {
    canAdd?: boolean;
    errors?: ValidationErrors;
    isProcessActivity?: boolean;
    onChange: (data: IEpisodesAndJournals, validateKeys?: string | string[]) => void;
    patient: IPatient;
    processActivityId?: string | null;
    requestId?: string;
    variant: 'outline' | 'flushed';
}

export function EpisodeJournalsAddEdit(props: IEpisodeJournalsAddEditProps) {
    const { canAdd, errors, patient, variant, isProcessActivity } = props;
    const [episodes, setEpisodes] = useState<Episodes>();
    const [areEpisodesLoaded, setAreEpisodesLoaded] = useState<boolean>(false);
    const [loadFailed, setLoadFailed] = useState<boolean>(false);
    const [journalEntries, setJournalEntries] = useState<JournalEntries>(OrderedMap());
    const [isEpisodesAndJournalsOpen, setIsEpisodesAndJournalsOpen] = useState(false);
    const [selectedEpisodesCount, setSelectedEpisodesCount] = useState(0);
    const [hasHisEpisodesAndJournals, setHasHisEpisodesAndJournals] = useState(false);
    const [validationKeys, setValidationKeys] = useState<string[]>([]);

    const translate = getSelectedLanguage();

    useEffect(() => {
        checkIfDataFromHis();
        loadEpisodesAndJournals();
    }, []);

    useEffect(() => {
        const episodesAndJournals: IEpisodesAndJournals = {
            episodes: filterAndPrepareEpisodesForSaving(episodes),
            journalEntries: prepareEpisodesForSaving(journalEntries),
        };
        props.onChange(episodesAndJournals, validationKeys || 'episodesAndJournals');
        setSelectedEpisodesCount(getNumberOfEpisodesSelected());
    }, [episodes, journalEntries]);

    useEffect(() => {
        if (canAdd && episodes && !hasHisEpisodesAndJournals) {
            setEpisodes(setActionPickForAddedInitialEpisodesAndJournals(episodes));
        }
    }, [episodes, hasHisEpisodesAndJournals]);

    return (
        <>
            {canAdd && !hasHisEpisodesAndJournals ? (
                <EpisodeJournalsCanAdd
                    addEmptyEpisodeJournals={addEmptyEpisodeJournals}
                    episodes={episodes}
                    errors={errors}
                    journalEntries={journalEntries}
                    renderJournalEntries={renderJournalEntries}
                    selectedEpisodesCount={selectedEpisodesCount}
                    setEpisodes={setEpisodes}
                    setJournalEntries={setJournalEntries}
                    setValidationKeys={setValidationKeys}
                    validationKeys={validationKeys}
                />
            ) : null}

            {hasHisEpisodesAndJournals ? (
                <ProfessionalSummaryDataSection
                    extraHeaderElements={
                        patientHasEpisodes() ? (
                            <Spacer
                                direction="horizontal"
                                spacing="small"
                            >
                                <LinkOnClick
                                    className="mr-2"
                                    onClick={async () => await selectOrDeselectAllEpisodes()}
                                >
                                    {translate.professionalSummary.all}
                                </LinkOnClick>
                                <LinkOnClick onClick={() => selectActiveEpisodes()}>
                                    {translate.professionalSummary.current}
                                </LinkOnClick>
                            </Spacer>
                        ) : undefined
                    }
                    isOpen={isEpisodesAndJournalsOpen}
                    onToggle={() => {
                        if (!checkJournalsHaveErrors(props.errors)) {
                            setIsEpisodesAndJournalsOpen(!isEpisodesAndJournalsOpen);
                        }
                    }}
                    professionalSummaryDataType={ProfessionalSummaryDataType.episodesAndJournals}
                    selectionCount={selectedEpisodesCount}
                >
                    {patientHasEpisodes() && (
                        <>
                            <ProfessionalSummaryDataTable>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader></TableHeader>
                                        <TableHeader>{translate.episodes.displayName}</TableHeader>
                                        <TableHeader>{translate.episodes.description}</TableHeader>
                                        <TableHeader>{translate.episodes.icpc}</TableHeader>
                                        <TableHeader align="right">{translate.common.startDate}</TableHeader>
                                        <TableHeader align="right">{translate.common.endDate}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>{renderEpisodes()}</TableBody>
                            </ProfessionalSummaryDataTable>

                            {selectedEpisodesCount > 0 ? renderJournalEntries() : null}

                            {selectedEpisodesCount > 0 && selectedEpisodesCount > (journalEntries?.size || 0) ? (
                                <Button
                                    iconLeft="add"
                                    onClick={addEmptyEpisodeJournals}
                                    variant="link"
                                >
                                    Voeg extra journaalregel toe
                                </Button>
                            ) : null}

                            <ProfessionalSummaryDataPlaceholder
                                hasContent={patientHasEpisodes()}
                                isLoading={!areEpisodesLoaded}
                                loadFailed={loadFailed}
                                messageFailed={translate.professionalSummary.loadError.replace(
                                    /\{0\}/g,
                                    `professionalSummary.${ProfessionalSummaryDataType.episodesAndJournals}`
                                )}
                                messageNotFound={translate.professionalSummary.noEpisodesFound}
                            />
                        </>
                    )}

                    {!patientHasEpisodes() && areEpisodesLoaded && (
                        <Alert appearance="warning">{translate.professionalSummary.noEpisodesFound}</Alert>
                    )}
                </ProfessionalSummaryDataSection>
            ) : null}
        </>
    );

    function renderEpisodes() {
        if (!episodes || episodes.size === 0) {
            return;
        }

        const episodesRows = [];

        for (const [key, episode] of episodes.entrySeq().toArray() as [string, EpisodeRecord][]) {
            episodesRows.push(
                <TableRow key={episode.id}>
                    <TableData style={{ width: '36px', minWidth: '36px' }}>
                        <Checkbox
                            checked={episode.action === ProfessionalSummaryDataAction.pick}
                            onChange={async (event) => {
                                await onChange(
                                    episodes.setIn(
                                        [key, 'action'],
                                        event.target.checked
                                            ? ProfessionalSummaryDataAction.pick
                                            : ProfessionalSummaryDataAction.skip
                                    )
                                );
                            }}
                        />
                    </TableData>
                    <TableData>{episode.valueDisplayName}</TableData>
                    <TableData>{episode.comment}</TableData>
                    <TableData>{episode.codeCode}</TableData>
                    <TableData
                        align="right"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        <NullableFormattedDate value={episode.startDate} />
                    </TableData>
                    <TableData
                        align="right"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        <NullableFormattedDate value={episode.endDate} />
                    </TableData>
                </TableRow>
            );
        }

        return episodesRows;
    }

    function renderJournalEntries() {
        if (!journalEntries || journalEntries.size === 0) {
            return;
        }

        const journalEntryRows = [];
        const allEpisodes = episodes?.valueSeq().toJS();

        for (const [key, journalEntry] of journalEntries.entrySeq().toArray() as [string, IJournal][]) {
            journalEntryRows.push(
                <EpisodeJournalEntryAddEdit
                    availableEpisodes={getAvailableEpisodesForJournalEntries()}
                    episodes={allEpisodes}
                    errors={props.errors}
                    journalEntries={journalEntries}
                    journalEntry={journalEntry}
                    journalEntryId={key}
                    key={key}
                    onChange={handleChangeJournal}
                    onChangeEpisode={handleChangeEpisode}
                    onDelete={handleDeleteJournal}
                    variant={variant}
                />
            );
        }

        return journalEntryRows;
    }

    function setActionPickForAddedInitialEpisodesAndJournals(initialEpisodes: Episodes) {
        for (const [key] of initialEpisodes.entrySeq().toArray() as [string, EpisodeRecord][]) {
            initialEpisodes = initialEpisodes.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
        }

        return initialEpisodes;
    }

    async function loadEpisodesAndJournals() {
        let initialEpisodes;

        if (isProcessActivity) {
            try {
                initialEpisodes = await loadProcessActivityEpisodes(patient, props.processActivityId || null);

                if (initialEpisodes) {
                    const processActivityJournalEntriesResponse = await fetchJson({
                        method: 'GET',
                        url: `/api/processActivities/${props.processActivityId}/journalEntries`,
                    });

                    setJournalEntries(
                        prepareAndSetJournalEntries(processActivityJournalEntriesResponse.json.items, initialEpisodes)
                    );
                }
            } catch (error) {
                setLoadFailed(true);
            }
        } else {
            try {
                initialEpisodes = await loadRequestEpisodes(patient, props.requestId);

                if (initialEpisodes && props.requestId) {
                    const requestJournalEntriesResponse = await fetchJson({
                        method: 'GET',
                        url: `/api/requests/${props.requestId}/requestJournalEntries`,
                    });

                    setJournalEntries(
                        prepareAndSetJournalEntries(requestJournalEntriesResponse.json.items, initialEpisodes)
                    );
                }
            } catch (error) {
                setLoadFailed(true);
            }
        }

        setEpisodes(initialEpisodes);
        setAreEpisodesLoaded(true);
        setLoadFailed(false);
    }

    async function loadEpisodeJournalEntries(episodeId: string) {
        const result = await fetchJson({ method: 'GET', url: `/api/patients/${episodeId}/journals` });
        if (result.json.total > 0) {
            return result.json.items;
        }

        return [];
    }

    async function checkIfDataFromHis() {
        setHasHisEpisodesAndJournals(await isHisDataAvailable(patient.id));
    }

    function addEmptyEpisodeJournals() {
        setJournalEntries(journalEntries?.set(uuidv4(), { episodeValueDisplayName: '' } as IJournal));
    }

    async function handleChangeEpisode(newEpisodeId: string | null, journalEntryId: string | null) {
        let updatedJournalEntries = journalEntries;

        if (newEpisodeId) {
            let newJournal: IJournal;

            const selectedEpisode = episodes?.find((episode) => episode?.id === newEpisodeId)?.toJS();
            newJournal = { episodeValueDisplayName: selectedEpisode.valueDisplayName };

            const episodeJournalEntries = await loadEpisodeJournalEntries(newEpisodeId);

            episodeJournalEntries.forEach((journalEntry: IJournalEntry) => {
                newJournal = { ...newJournal, [journalEntry.entryDisplayName]: journalEntry.comment };
            });

            updatedJournalEntries = journalEntries?.set(journalEntryId ?? uuidv4(), newJournal);
            setJournalEntries(updatedJournalEntries);
        }
    }

    async function handleChangeJournal(journal: IJournal, journalEntryKey: string) {
        setJournalEntries(journalEntries?.set(journalEntryKey, journal));
    }

    async function handleDeleteJournal(journalEntryKey: string) {
        setJournalEntries(journalEntries?.delete(journalEntryKey));
    }

    function areAllEpisodesSelected() {
        return episodes ? episodes.every((episode) => episode?.action === ProfessionalSummaryDataAction.pick) : false;
    }

    function getNumberOfEpisodesSelected() {
        return episodes ? episodes.filter((episode) => episode?.action === ProfessionalSummaryDataAction.pick).size : 0;
    }

    function patientHasEpisodes() {
        return areEpisodesLoaded && episodes !== undefined && episodes.size > 0;
    }

    async function selectOrDeselectAllEpisodes() {
        const allEpisodesSelected = areAllEpisodesSelected();

        const action = allEpisodesSelected ? ProfessionalSummaryDataAction.skip : ProfessionalSummaryDataAction.pick;
        const updateEpisodes = episodes
            ? episodes.map((episode) => {
                  if (episode) {
                      return episode.set('action', action);
                  }
              })
            : undefined;

        await onChange(updateEpisodes as Episodes);
    }

    async function onChange(changedEpisodes: Episodes) {
        setEpisodes(changedEpisodes);
    }

    function selectActiveEpisodes() {
        if (episodes) {
            const updatedEpisodes = episodes.map((episode) => {
                if (episode) {
                    return isCurrentEpisode(episode)
                        ? episode.set('action', ProfessionalSummaryDataAction.pick)
                        : episode.set('action', ProfessionalSummaryDataAction.skip);
                }
            });
            setEpisodes(updatedEpisodes as Episodes);
        }
    }

    function getAvailableEpisodesForJournalEntries(): Array<IEpisode> {
        if (!episodes) {
            return [];
        }

        const availableEpisodes: Array<IEpisode> = episodes
            .valueSeq()
            .filter((episode) => {
                if (!episode) {
                    return false;
                } else {
                    return episode.action === ProfessionalSummaryDataAction.pick;
                }
            })
            .toJS();

        return availableEpisodes;
    }
}
